/**
 * Chat — text conversation with the main agent (Vooice).
 *
 * Routes: /app/chat. Talks to /api/chat/* via useDirectChat. Keeps the
 * existing Claude OAuth setup flow + widget-action dispatch from the
 * previous implementation; restyle is the only structural change.
 *
 * Top-bar "Call her" button → /app/calls/test for a WebRTC voice session
 * mid-conversation, when text isn't enough.
 */
import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  useDirectChat,
  useChatConversationList,
  useChatConversation,
} from "../hooks/useDirectChat";
import { useWidgets, type Widget } from "../context/WidgetContext";
import { claudeAuth, setup, type SetupStatus } from "../api/client";
import type { WidgetAction } from "../api/types";
import { Button, Card, LiveDot, Mono, Pill } from "../components/v3";

let widgetOffset = 0;

function getNextPosition(): { x: number; y: number } {
  const offset = (widgetOffset++ % 5) * 40;
  return { x: 80 + offset, y: 80 + offset };
}

export function Chat() {
  const {
    conversationId,
    setConversationId,
    sending,
    error,
    clearError,
    sendMessage,
    startConversation,
    deleteConversation,
  } = useDirectChat();

  const queryClient = useQueryClient();
  const { data: conversations } = useChatConversationList();
  const { data: currentConv } = useChatConversation(conversationId);
  const { dispatch } = useWidgets();

  const { data: setupStatus } = useQuery({
    queryKey: ["setup-status"],
    queryFn: () => setup.status(),
    staleTime: 30_000,
  });

  const [input, setInput] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [oauthUrl, setOauthUrl] = useState<string | null>(null);
  const [displayCode, setDisplayCode] = useState("");
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { data: authStatus } = useQuery({
    queryKey: ["claude-auth", "status"],
    queryFn: () => claudeAuth.status(),
    refetchInterval: 30_000,
  });

  const needsLogin = authStatus && !authStatus.loggedIn;

  async function handleStartOAuth() {
    if (authSubmitting) return;
    setAuthSubmitting(true);
    setAuthError(null);
    try {
      const result = await claudeAuth.start();
      setOauthUrl(result.url);
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : "Failed to start OAuth flow");
    }
    setAuthSubmitting(false);
  }

  async function handleCompleteOAuth() {
    if (!displayCode.trim() || authSubmitting) return;
    setAuthSubmitting(true);
    setAuthError(null);
    try {
      const result = await claudeAuth.complete(displayCode.trim());
      if (result.loggedIn) {
        setDisplayCode("");
        setOauthUrl(null);
        queryClient.invalidateQueries({ queryKey: ["claude-auth", "status"] });
      } else {
        setAuthError("Auth completed but login check failed. Try again.");
      }
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : "Failed to complete OAuth");
    }
    setAuthSubmitting(false);
  }

  const messages = currentConv?.messages ?? [];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, pendingMessage, sending]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [conversationId]);

  function processWidgetActions(actions: WidgetAction[]) {
    for (const action of actions) {
      switch (action.type) {
        case "create": {
          const pos = action.position ?? getNextPosition();
          const size = action.size ?? { w: 400, h: 320 };
          const widget: Widget = {
            id: action.widgetId,
            type: action.widgetType ?? "generic",
            title: action.title ?? "Widget",
            data: action.data ?? {},
            position: pos,
            size,
            minimized: false,
            zIndex: 0,
          };
          dispatch({ type: "CREATE", widget });
          break;
        }
        case "update":
          if (action.data) {
            dispatch({ type: "UPDATE", widgetId: action.widgetId, data: action.data });
          }
          break;
        case "close":
          dispatch({ type: "CLOSE", widgetId: action.widgetId });
          break;
      }
    }
  }

  async function handleSend() {
    if (!input.trim() || sending) return;
    const content = input;
    setInput("");
    setPendingMessage(content);
    try {
      const response = await sendMessage(content);
      if (response?.widget_actions?.length) {
        processWidgetActions(response.widget_actions);
      }
    } finally {
      setPendingMessage(null);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const showWelcome = !conversationId && !sending && !pendingMessage;

  return (
    <div className="flex flex-col h-full relative">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-line shrink-0 bg-bg/30">
        <div className="flex items-center gap-3 min-w-0">
          <LiveDot tone="amber" />
          <h1 className="text-sm font-medium text-t1">Vooice</h1>
          {conversationId && currentConv?.title && (
            <span className="text-xs text-t3 truncate font-display italic">
              · {currentConv.title}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button as="a" href="/app/calls/test" variant="amber" size="sm">
            Call her
          </Button>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-xs text-t3 hover:text-t1 px-3 py-1.5 rounded-md hover:bg-white/5 transition-colors"
          >
            History
          </button>
          <button
            onClick={async () => {
              const conv = await startConversation();
              setConversationId(conv.id);
            }}
            className="text-xs text-t3 hover:text-t1 px-3 py-1.5 rounded-md hover:bg-white/5 transition-colors"
          >
            New
          </button>
        </div>
      </div>

      {/* History dropdown */}
      {showHistory && (
        <div className="absolute top-12 right-6 w-80 glass rounded-lg shadow-bezel z-50 max-h-96 overflow-y-auto">
          {(conversations ?? []).map((conv) => (
            <div
              key={conv.id}
              className={[
                "group flex items-center border-b border-line-soft last:border-b-0",
                conversationId === conv.id ? "bg-white/[0.04]" : "hover:bg-white/[0.02]",
              ].join(" ")}
            >
              <button
                onClick={() => {
                  setConversationId(conv.id);
                  setShowHistory(false);
                }}
                className="flex-1 text-left px-4 py-3 text-sm min-w-0"
              >
                <div className="truncate text-t1">{conv.title || conv.id.slice(5, 17)}</div>
                <Mono className="block mt-1">
                  {new Date(conv.updated_at).toLocaleDateString()}
                </Mono>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm("Delete this conversation?"))
                    deleteConversation(conv.id);
                }}
                className="px-3 py-1 text-t4 hover:text-rose opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Delete conversation"
              >
                ×
              </button>
            </div>
          ))}
          {(!conversations || conversations.length === 0) && (
            <div className="px-4 py-6 text-sm text-t3 text-center">No conversations yet</div>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        {showWelcome ? (
          <WelcomeScreen
            setupStatus={setupStatus}
            needsLogin={!!needsLogin}
            authSubmitting={authSubmitting}
            authError={authError}
            oauthUrl={oauthUrl}
            displayCode={displayCode}
            onDisplayCodeChange={setDisplayCode}
            onStartOAuth={handleStartOAuth}
            onCompleteOAuth={handleCompleteOAuth}
            onResetOAuth={() => {
              setOauthUrl(null);
              setAuthError(null);
            }}
          />
        ) : (
          <div className="max-w-[760px] mx-auto px-6 py-6 space-y-4">
            {messages.map((msg) => (
              <MessageBubble key={msg.id} role={msg.role} content={msg.content} model={msg.model} />
            ))}

            {pendingMessage &&
              !messages.some(
                (m) => m.role === "user" && m.content === pendingMessage,
              ) && (
                <MessageBubble role="user" content={pendingMessage} pending />
              )}

            {sending && <ThinkingIndicator />}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="mx-6 mb-3 flex items-center justify-between border border-rose/30 bg-rose/[0.06] rounded-md px-3 py-2 text-sm text-rose">
          <span>{error}</span>
          <button onClick={clearError} className="ml-2 hover:text-t1">
            ×
          </button>
        </div>
      )}

      {/* Input */}
      <div className="shrink-0 border-t border-line p-5 bg-bg/30">
        <div className="max-w-[760px] mx-auto">
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Vooice anything…"
              rows={1}
              className="flex-1 px-4 py-3 bg-bg-2 border border-line rounded-lg text-t1 placeholder:text-t4 focus:outline-none focus:border-amber/40 resize-none text-sm transition-colors"
              disabled={sending}
            />
            <Button
              variant="amber"
              size="md"
              onClick={handleSend}
              disabled={sending || !input.trim()}
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Message bubble ─────────────────────────────────────────────────────────

function MessageBubble({
  role,
  content,
  model,
  pending,
}: {
  role: string;
  content: string;
  model?: string | null;
  pending?: boolean;
}) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={[
          "max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed",
          isUser
            ? "bg-paper text-bg"
            : "bg-white/[0.03] border border-line text-t1",
          pending && "opacity-70",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="whitespace-pre-wrap">{content}</div>
        {model && !isUser && (
          <Mono className="block mt-2 text-right text-t4">{model}</Mono>
        )}
      </div>
    </div>
  );
}

function ThinkingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-white/[0.03] border border-line rounded-2xl px-4 py-3 inline-flex items-center gap-2">
        <span className="size-1.5 rounded-full bg-amber animate-pulse-amber" />
        <span className="text-sm text-t2 animate-pulse">Thinking…</span>
      </div>
    </div>
  );
}

// ── Welcome screen ─────────────────────────────────────────────────────────

function WelcomeScreen({
  setupStatus,
  needsLogin,
  authSubmitting,
  authError,
  oauthUrl,
  displayCode,
  onDisplayCodeChange,
  onStartOAuth,
  onCompleteOAuth,
  onResetOAuth,
}: {
  setupStatus?: SetupStatus;
  needsLogin: boolean;
  authSubmitting: boolean;
  authError: string | null;
  oauthUrl: string | null;
  displayCode: string;
  onDisplayCodeChange: (s: string) => void;
  onStartOAuth: () => void;
  onCompleteOAuth: () => void;
  onResetOAuth: () => void;
}) {
  return (
    <div className="flex items-center justify-center h-full px-6 py-10">
      <div className="text-center max-w-lg w-full">
        <h2 className="font-display text-4xl mb-2 leading-tight">
          What can I <span className="font-display-italic">help</span> with?
        </h2>
        <p className="text-sm text-t2 mb-8">
          Type a request, or call her if it's faster to say it out loud.
        </p>

        {/* Setup checklist */}
        {setupStatus && !needsLogin && (
          <Card padding="md" className="text-left mb-6">
            <Mono className="block mb-3">Connections</Mono>
            <div className="space-y-2">
              <SetupRow
                label="AI"
                ok={setupStatus.llm.status === "connected"}
                detail={setupStatus.llm.model}
              />
              <SetupRow
                label="Google Calendar"
                ok={setupStatus.google.status === "connected"}
                href="/app/settings#google"
              />
              <SetupRow
                label="Telegram"
                ok={setupStatus.telegram.status === "connected"}
              />
              <SetupRow
                label="Voice"
                ok={setupStatus.voice.status === "configured"}
                href="/app/settings#voice"
              />
              <SetupRow
                label="Email"
                ok={setupStatus.email.status === "configured"}
              />
            </div>
          </Card>
        )}

        {/* Claude auth */}
        {needsLogin && (
          <Card padding="md" className="text-left border-amber/40">
            <div className="flex items-center gap-2 mb-3">
              <LiveDot />
              <Mono tone="amber">Claude authentication required</Mono>
            </div>
            <div className="space-y-3">
              {!oauthUrl ? (
                <>
                  <p className="text-xs text-t2">
                    Sign in with your Claude account to enable the AI assistant.
                  </p>
                  <Button
                    variant="amber"
                    size="md"
                    onClick={onStartOAuth}
                    disabled={authSubmitting}
                    className="w-full"
                  >
                    {authSubmitting ? "Starting…" : "Sign in to Claude"}
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-xs text-t2">
                    1. Open the link below and authorize access:
                  </p>
                  <a
                    href={oauthUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-bg-2 border border-line rounded-md px-3 py-2 text-xs text-amber hover:text-amber-deep font-mono break-all"
                  >
                    Open Claude sign-in
                  </a>
                  <p className="text-xs text-t2">
                    2. After authorizing, paste the code shown:
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={displayCode}
                      onChange={(e) => onDisplayCodeChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onCompleteOAuth();
                      }}
                      placeholder="Paste code…"
                      className="flex-1 px-3 py-2 bg-bg-2 border border-line rounded-md text-t1 text-xs placeholder:text-t4 focus:outline-none focus:border-amber/50 font-mono"
                    />
                    <Button
                      variant="amber"
                      size="sm"
                      onClick={onCompleteOAuth}
                      disabled={!displayCode.trim() || authSubmitting}
                    >
                      {authSubmitting ? "…" : "Submit"}
                    </Button>
                  </div>
                  <button
                    onClick={onResetOAuth}
                    className="text-xs text-t3 hover:text-t1 transition-colors"
                  >
                    Start over
                  </button>
                </>
              )}
              {authError && (
                <div className="text-xs text-rose">{authError}</div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function SetupRow({
  label,
  ok,
  detail,
  href,
}: {
  label: string;
  ok: boolean;
  detail?: string;
  href?: string;
}) {
  const inner = (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <div className="flex items-center gap-3">
        <span
          className={`size-1.5 rounded-full ${ok ? "bg-amber" : "bg-t4"}`}
        />
        <span className={ok ? "text-t1 text-sm" : "text-t3 text-sm"}>
          {label}
        </span>
      </div>
      {ok && detail ? (
        <Mono>{detail}</Mono>
      ) : !ok && href ? (
        <Pill tone="amber">Set up</Pill>
      ) : !ok ? (
        <Mono>Off</Mono>
      ) : null}
    </div>
  );
  if (!ok && href) {
    return (
      <Link
        to={href}
        className="block hover:bg-white/[0.02] -mx-2 px-2 rounded-md transition-colors"
      >
        {inner}
      </Link>
    );
  }
  return <div className="-mx-2 px-2">{inner}</div>;
}
